import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, X } from 'lucide-react'
import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'

/**
 * ConnectivityBanner - Warns users if API requests are blocked by browser extensions
 *
 * Detects ERR_BLOCKED_BY_CLIENT errors and displays a dismissible banner
 * to inform users about potential ad blocker conflicts.
 */
export function ConnectivityBanner() {
  const [dismissed, setDismissed] = useState(false)
  const [hasQueryClient, setHasQueryClient] = useState(false)

  // Check if QueryClient is available (handles SSR/hydration edge cases)
  useEffect(() => {
    setHasQueryClient(true)
  }, [])

  // Don't render hook-dependent content until QueryClient is confirmed available
  if (!hasQueryClient) {
    return null
  }

  return <ConnectivityBannerInner dismissed={dismissed} setDismissed={setDismissed} />
}

function ConnectivityBannerInner({
  dismissed,
  setDismissed
}: {
  dismissed: boolean
  setDismissed: (v: boolean) => void
}) {
  // Test connectivity to Supabase
  const { error, isError } = useQuery<boolean, Error>({
    queryKey: ['connectivity-check'],
    queryFn: async () => {
      const { error } = await supabase.from('site_settings').select('id').limit(1)
      if (error) throw error
      return true
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 60000, // Check every minute
  })

  // Check if error is due to blocked request
  const isBlocked = isError && error?.message?.includes('Failed to fetch')

  if (!isBlocked || dismissed) {
    return null
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-garage-red text-garage-cream shadow-lg">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-sm">
              Connection Issue Detected
            </p>
            <p className="text-xs text-garage-cream/90 mt-0.5">
              Your browser or an extension (ad blocker) may be blocking API requests to localhost:54321.
              Please disable ad blockers or add this site to your allowlist for full functionality.
            </p>
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 p-1 hover:bg-garage-dark/20 rounded transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  )
}
